import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";

import {
  GET_MY_SUBSCRIPTION,
  GetMySubscriptionResponse,
} from "../pages/subscription/queries";

interface PaymentWarningBarProps {
  clientId: string;
}

/**
 * Clients that should never see the payment warning bar regardless of their
 * subscription status (internal / demo / comped accounts).
 */
const EXEMPT_CLIENT_IDS = ["9"];

/**
 * Days of continued access we grant a partner after their subscription payment
 * lapses before the tone of the warning turns into "service will be suspended".
 *
 * - Stripe (auto-pay) subs: Stripe keeps retrying the charge during this window
 *   and the subscription stays `past_due` (not `cancelled`), so the partner
 *   keeps full access the whole time.
 * - Manual (in-person) subs: there is no automatic collection, so the grace is
 *   just the runway the partner gets to come pay before an admin steps in.
 *
 * This only controls the copy shown in the bar — access itself is not cut here.
 */
const GRACE_DAYS = 5;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const BAR_STYLE: React.CSSProperties = {
  backgroundColor: "#f78f03",
  color: "#000000",
  height: "48px",
  padding: "0 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  fontSize: "14px",
  lineHeight: 1.2,
  width: "100%",
  zIndex: 10000,
  position: "fixed",
  top: 0,
  left: 0,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
};

const LINK_STYLE: React.CSSProperties = {
  color: "#000000",
  textDecoration: "underline",
  marginLeft: "8px",
  whiteSpace: "nowrap",
};

const formatDate = (d: Date) =>
  d.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
  });

const PaymentWarningBar: React.FC<PaymentWarningBarProps> = ({ clientId }) => {
  const { data } = useQuery<GetMySubscriptionResponse>(GET_MY_SUBSCRIPTION, {
    fetchPolicy: "cache-and-network",
    // The bar renders on every authenticated screen; poll occasionally so it
    // clears itself shortly after the partner pays without a manual refresh.
    pollInterval: 5 * 60 * 1000,
  });

  const subscription = data?.getMySubscription;

  // `period_end` is the end of the last paid period — i.e. the date the current
  // payment is/was due.
  const periodEnd = subscription?.period_end
    ? new Date(subscription.period_end)
    : null;
  const periodEndValid = !!periodEnd && !isNaN(periodEnd.getTime());
  const periodEndPassed = periodEndValid && periodEnd!.getTime() < Date.now();

  // A manual (in-person) subscription has no Stripe subscription behind it, so
  // it never flips to `past_due` on its own — it just sits at `active` with a
  // `period_end` in the past once it lapses.
  const isManualSub = !!subscription && !subscription.stripe_subscription_id;

  // Warn when the subscription payment is overdue, and never for exempt clients:
  //  - Stripe auto-pay: Stripe marks the sub `past_due` after a failed charge.
  //  - Manual: `active` but past its paid period.
  // `active` (still within period) subs, partners with no subscription, and
  // trial partners all see nothing.
  const paymentOverdue =
    !!subscription &&
    (subscription.status === "past_due" ||
      (isManualSub && subscription.status === "active" && periodEndPassed));

  const visible = !EXEMPT_CLIENT_IDS.includes(clientId) && paymentOverdue;

  // While the bar is visible, push the fixed header + content down (see the
  // `body.has-payment-warning` rules in app.css) so it doesn't cover the nav.
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-payment-warning");
    return () => document.body.classList.remove("has-payment-warning");
  }, [visible]);

  if (!visible || !subscription) {
    return null;
  }

  // Grace runs from `period_end` (the payment due date).
  const graceDeadline = periodEndValid
    ? new Date(periodEnd!.getTime() + GRACE_DAYS * MS_PER_DAY)
    : null;

  const daysLeft = graceDeadline
    ? Math.ceil((graceDeadline.getTime() - Date.now()) / MS_PER_DAY)
    : null;

  const withinGrace = daysLeft === null || daysLeft > 0;

  // Manual (in-person) subs never had a failed charge — the payment is simply
  // due — so the copy talks about a pending payment rather than a failed one.
  const lead = isManualSub
    ? "El pago de tu suscripción está pendiente."
    : "No pudimos procesar el pago de tu suscripción.";

  let message: React.ReactNode;
  if (withinGrace && daysLeft !== null) {
    const deadlineText = graceDeadline
      ? ` (antes del ${formatDate(graceDeadline)})`
      : "";
    message = (
      <span>
        {`⚠️ ${lead} Tienes `}
        <strong>{`${daysLeft} ${daysLeft === 1 ? "día" : "días"}`}</strong>
        {` para regularizar tu pago${deadlineText} y evitar la suspensión del servicio.`}
      </span>
    );
  } else if (withinGrace) {
    // Overdue but no usable period_end date — generic warning.
    message = (
      <>⚠️ {lead} Regulariza tu pago para evitar la suspensión del servicio.</>
    );
  } else {
    message = (
      <>
        ⚠️ El pago de tu suscripción sigue pendiente. Tu servicio puede ser
        suspendido en cualquier momento. Regulariza tu pago lo antes posible.
      </>
    );
  }

  return (
    <div style={BAR_STYLE}>
      {message}
      {!isManualSub && (
        <Link to="/subscription" style={LINK_STYLE}>
          Pagar ahora
        </Link>
      )}
    </div>
  );
};

export default PaymentWarningBar;
